export const REVENUE_STATUS = {
  PENDING: 'Pending',
  VERIFIED: 'Verified',
  PAID: 'Paid',
} as const;

export const PAYOUT_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCESSFUL: 'successful',
  FAILED: 'failed',
} as const;

export const INDIA_COUNTRY_CODE = 'IN';

export const PAYOUT_METHOD = {
  BANK: 'bank',
  PAYPAL: 'paypal',
} as const;

export const PAN_DOCUMENT_STATUS = {
  PENDING_REVIEW: 'pending_review',
  VERIFIED: 'verified',
  REJECTED: 'rejected',
} as const;

export const PAN_DOCUMENT_MAX_SIZE_MB = 10;
export const PAN_DOCUMENT_MAX_SIZE_BYTES = PAN_DOCUMENT_MAX_SIZE_MB * 1024 * 1024;
export const PAN_DOCUMENT_VIEW_EXPIRY_MINUTES = 30;
export const PAN_DOCUMENT_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export const PAN_DOCUMENT_ACCEPT = PAN_DOCUMENT_ALLOWED_MIME_TYPES.join(',');

export type RevenueStatus = typeof REVENUE_STATUS[keyof typeof REVENUE_STATUS];
export type PayoutStatus = typeof PAYOUT_STATUS[keyof typeof PAYOUT_STATUS];
export type PayoutMethod = typeof PAYOUT_METHOD[keyof typeof PAYOUT_METHOD];
export type PanDocumentStatus = typeof PAN_DOCUMENT_STATUS[keyof typeof PAN_DOCUMENT_STATUS];

// Revenue Interfaces
export interface DailyRevenueSummary {
  _id: string;
  creatorId: string;
  date: Date;
  total_revenue: number;
  click_count: number;
  application_count?: number;
  currency: string;
  status: RevenueStatus;
  createdAt: Date;
  updatedAt: Date;
}

// Payout Interfaces
export type PayoutDetails =
  | {
      method: 'bank';
      country: string;
      currency: string;
      account_type?: 'savings' | 'current';
      address?: string;
      details: {
        beneficiary_name: string;
        bank_account: string;
        bank_name?: string;
        ifsc?: string;
        routing_number?: string;
        swift_code?: string;
        [key: string]: any;
      };
    }
  | {
      method: 'paypal';
      country: string;
      currency: string;
      address?: string;
      details: {
        paypal_email: string;
        [key: string]: any;
      };
    };

export interface PayoutReceipt {
  receipt_id: string;
  file_url: string;
  generated_at: Date;
}

export interface PayoutTransaction {
  _id: string;
  creatorId: string;
  payout_date: Date;
  amount: number;
  currency: string;
  payout_method: PayoutMethod;
  status: PayoutStatus;
  reference_id?: string;
  note?: string;
  payment_details: PayoutDetails;
  receipt?: PayoutReceipt;
  created_at: Date;
  updated_at: Date;
}

// Query Interfaces
export interface RevenueTimeSeriesQuery {
  creatorId: string;
  groupBy: 'week' | 'month';
  page?: number;
  limit?: number;
}

export interface PayoutListQuery {
  creatorId: string;
  page?: number;
  limit?: number;
  status?: PayoutStatus;
  payout_date?: string;
  search?: string;
}

// Response Interfaces
export interface RevenueMetrics {
  totalRevenue: number;
  totalClicks: number;
  pendingPayouts: number;
  successfulPayouts: number;
  currentMonthRevenue: number;
  lastMonthRevenue: number;
  grossEarnings: number;
  platformFees: number;
  pendingAmount: number;
  dueAmount: number;
  revenueGrowth: number;
  clicksGrowth?: number;
  currency?: string;
  // Advanced metrics from ClickHouse integration
  fourWeekClicks?: number;
  fourWeekClicksChange?: number;
  remainingPayout?: number;
  firstDollarDate?: Date;
  hundredDollarBuckets?: number;
  nextMilestone?: number;
  rpc?: number;
  rpa?: number;
  rpaGrowth?: number;
}

export interface RevenueTimeSeriesData {
  period: string;
  revenue: number;
  clicks: number;
  applications: number;
  date: Date;
}

export interface PaginatedPayouts {
  data: PayoutTransaction[];
  itemCount: number;
  perPage: number;
  currentPage: number;
  pageCount: number;
}

export interface LeaderboardEntry {
  creatorId: string;
  name: string;
  avatarUrl?: string;
  earnings: number;
  rank: number;
  isAnonymous: boolean;
  weeklyJobShareCount?: number;
}

export interface PaginatedLeaderboard {
  topCreators: LeaderboardEntry[];
  rankings: LeaderboardEntry[];
  currentUser?: {
    rank: number;
    earnings: number;
    isAnonymous: boolean;
  };
  pagination: {
    itemCount: number;
    perPage: number;
    currentPage: number;
    pageCount: number;
  };
}
